<template>
  <div style="background-color:#4a5568; overflow:hidden;">
    <!-- Loader -->
    <v-container
      v-if="loading"
      class="fill-height d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="#ff3b47" size="64" />
    </v-container>

    <!-- Main content -->
    <div v-else>
      <!-- Welcome Header -->
      <v-container class="mb-5">
        <v-row class="text-center">
          <v-col cols="12">
            <!-- Back Button -->
            <div class="d-flex align-center justify-start mb-4">
              <v-btn 
                icon 
                class="back-button" 
                @click="confirmBackNavigation"
                style="background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px; color: white;"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </div>
            
            <v-card-title
              style="color:white; font-size:1.5rem;"
              class="font-weight-bold"
            >
              Sales Order Tracker
            </v-card-title>
          </v-col>
        </v-row>
      </v-container>

      <!-- Main Content Area -->
      <div
        class="pa-4"
        style="background-color:white; border-radius:20px 20px 0 0; min-height:70%;"
      >
        <!-- Header Section -->
        <v-row class="d-flex justify-space-between align-center mb-4">
          <div
            class="text-h4 font-weight-bold mx-6 my-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:1px;
                   text-shadow:2px 2px 4px rgba(0,0,0,.2);"
          >
            SO TRACKER
          </div>
          
          <!-- Add Button -->
          <div v-if="canAddSalesTracker" class="mx-6">
            <v-btn
              large
              @click="navigateToAddSalesTracker"
              style="background: #4a5568; color: white; border-radius: 8px; text-transform: none;"
              class="font-weight-medium"
            >
              <v-icon left>mdi-plus</v-icon>
              Add SO Card
            </v-btn>
          </div>
        </v-row>

        <!-- Search & Filter Section -->
        <div class="mx-6 mb-6">
          <div
            class="text-h6 font-weight-bold mb-3"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;"
          >
            Filter & Search
          </div>
          
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                dense 
                outlined
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search SO Number / Customer / Salesperson"
                hide-details
                style="border-radius: 8px;"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedStatus"
                :items="statusOptions"
                dense 
                outlined
                label="Filter by Status"
                prepend-inner-icon="mdi-filter"
                hide-details
                style="border-radius: 8px;"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Sales Orders Section -->
        <div class="mx-6">
          <div
            class="text-h6 font-weight-bold mb-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;"
          >
            Sales Orders
          </div>

          <!-- Cards Grid -->
          <div class="sales-orders-grid">
            <div
              v-for="(tracker, index) in filteredTrackers" 
              :key="tracker.id || index"
              class="so-card"
              :class="{ 'clickable': canEditSalesTracker }"
              @click="canEditSalesTracker ? openSalesTracker(tracker) : null"
            >
              <!-- Header Section -->
              <div class="so-card-header">
                <v-chip
                  :color="getStatusColor(tracker.status)"
                  dark
                  label
                  small
                  class="status-chip"
                >
                  {{ tracker.status || "Unknown" }}
                </v-chip>
                <!-- Info Button -->
                <button class="info-btn" @click.stop="openInfoModal(tracker)">
                  <v-icon small>mdi-information</v-icon>
                </button>
              </div>
              
              <!-- SO Number Badge -->
              <div class="px-4 pb-2">
                <v-chip
                  color="#4a5568"
                  dark
                  label
                  class="so-number-chip"
                >
                  SO: {{ tracker.salesOrderNumber || 'N/A' }}
                </v-chip>
              </div>
              
              <!-- Content Section -->
              <div class="so-card-content">
                <div class="info-row">
                  <span class="info-label">Customer:</span>
                  <span class="info-value">{{ getCustomerName(tracker) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Salesperson:</span>
                  <span class="info-value">{{ tracker.salesperson || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Expected:</span>
                  <span class="info-value">{{ tracker.arrivalDate || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Linked Shipments:</span>
                  <span class="info-value">{{ getLinkedShipments(tracker.salesOrderNumber) }}</span>
                </div>
              </div>
              
              <!-- Card Action -->
              <div v-if="canEditSalesTracker" class="so-card-actions">
                <v-btn
                  color="#4a5568"
                  dark
                  block
                  class="action-btn"
                  style="text-transform: none; border-radius: 6px;"
                >
                  <v-icon left small>mdi-cog</v-icon>
                  Manage SO
                </v-btn>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="filteredTrackers.length === 0" class="text-center mt-8">
            <div class="no-results">
              <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-file-document-outline</v-icon>
              <h3 style="color: #4a5568; margin-bottom: 8px;">No sales orders found</h3>
              <p style="color: #7f8c8d;">Try adjusting your search or filter criteria</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <v-dialog v-model="infoDialog.visible" max-width="700">
      <v-card style="border-radius: 12px;">
        <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">
          <span class="modal-title">SO: {{ infoDialog.tracker.salesOrderNumber }} Details</span>
          <div class="header-actions">
            <v-btn 
              v-if="infoDialog.tracker.pdfUrl"
              icon 
              @click="viewPDF(infoDialog.tracker.pdfUrl)"
              title="View SO PDF"
              class="mr-2"
              style="color: white;"
            >
              <v-icon>mdi-file-pdf-box</v-icon>
            </v-btn>
            <v-btn icon @click="infoDialog.visible = false" style="color: white;">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="modal-grid">
            <div class="modal-field">
              <div class="modal-field-label">SO Number</div>
              <div class="modal-field-value">{{ infoDialog.tracker.salesOrderNumber || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Customer</div>
              <div class="modal-field-value">{{ getCustomerName(infoDialog.tracker) }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">PO Number</div>
              <div class="modal-field-value">{{ infoDialog.tracker.poNumber || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Salesperson</div>
              <div class="modal-field-value">{{ infoDialog.tracker.salesperson || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Engineer</div>
              <div class="modal-field-value">{{ infoDialog.tracker.engineer || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Order Placed Date</div>
              <div class="modal-field-value">{{ infoDialog.tracker.orderPlacedDate || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Expected Arrival Date</div>
              <div class="modal-field-value">{{ infoDialog.tracker.arrivalDate || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Method</div>
              <div class="modal-field-value">{{ infoDialog.tracker.method || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Status</div>
              <div class="modal-field-value">
                <span class="status-badge" :class="getStatusClass(infoDialog.tracker.status)">
                  {{ infoDialog.tracker.status || 'Unknown' }}
                </span>
              </div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Linked Shipments</div>
              <div class="modal-field-value">{{ getLinkedShipments(infoDialog.tracker.salesOrderNumber) }}</div>
            </div>
          </div>
          
          <!-- Latest Combined Updates -->
          <div class="modal-field full-width mt-4" v-if="getCombinedUpdates(infoDialog.tracker).length">
            <div class="modal-field-label">Latest Updates</div>
            <div class="combined-updates">
              <div 
                v-for="(update, index) in getCombinedUpdates(infoDialog.tracker)" 
                :key="index"
                class="update-item"
              >
                <div class="update-header">
                  <v-chip
                    :color="update.source === 'SO' ? 'primary' : 'orange'"
                    dark
                    small
                    label
                    class="update-source"
                  >
                    {{ update.source }}
                  </v-chip>
                  <span v-if="update.shipmentNumber && update.source === 'Shipment'" class="update-shipment">
                    {{ update.shipmentNumber === 'OPEN_ORDER' ? 'Open Order' : `Shipment #${update.shipmentNumber}` }}
                  </span>
                  <span class="update-date">{{ formatUpdateDate(update.timestamp) }}</span>
                </div>
                <div class="update-text">{{ update.text }}</div>
                <div class="update-user">By: {{ update.user }}</div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-btn 
            color="#4a5568" 
            dark 
            block 
            @click="copyModalContent(infoDialog.tracker)"
            style="text-transform: none; border-radius: 8px;"
          >
            Copy All Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card style="border-radius: 12px;">
        <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">
          Confirm Navigation
        </v-card-title>
        <v-card-text class="pa-6">
          Are you sure you want to go back?
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="backDialog = false" style="text-transform: none;">Cancel</v-btn>
          <v-btn color="#4a5568" dark @click="goBack" style="text-transform: none; border-radius: 6px;">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const db = getFirestore(firebaseApp);

export default {
  name: "AllSalesTracker",
  data() {
    return {
      trackers: [],
      shipments: [],
      search: '',
      selectedStatus: '',
      statusOptions: [
        "All",
        "Open Order",
        "Referenced", 
        "Booked",
        "Manufacturing",
        "Partial Supply",
        "In Transit",
        "Docked",
        "At Airport", 
        "On Route to JHB",
        "Complete",
        "Delivered",
        "Clear"
      ],
      loading: true,
      backDialog: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      userRole: "",
      userPermissions: {},
      infoDialog: {
        visible: false,
        tracker: {}
      },
      isAdmin: false,
      loggedInUser: '',
      authStateUnsubscribe: null,
    };
  },

  computed: {
    canAddSalesTracker() {
      const hasPermission = this.userPermissions?.salesOrderTracker?.Add_Button || false;
      console.log('Add SO Permission Check:', {
        email: this.loggedInUser,
        role: this.userRole,
        hasPermission: hasPermission,
        salesOrderTracker: this.userPermissions?.salesOrderTracker
      });
      return hasPermission;
    },
    canEditSalesTracker() {
      return this.userPermissions?.salesOrderTracker?.Edit_Details || false;
    },
    filteredTrackers() {
      let filtered = this.trackers;
      
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(tracker =>
          (tracker.salesOrderNumber && tracker.salesOrderNumber.toString().toLowerCase().includes(searchLower)) ||
          (tracker.supplierName && tracker.supplierName.toLowerCase().includes(searchLower)) ||
          (tracker.customerName && tracker.customerName.toLowerCase().includes(searchLower)) ||
          (tracker.status && tracker.status.toLowerCase().includes(searchLower)) ||
          (tracker.salesperson && tracker.salesperson.toLowerCase().includes(searchLower))
        );
      }
      
      if (this.selectedStatus && this.selectedStatus !== "All" && this.selectedStatus !== "Clear") {
        filtered = filtered.filter(tracker => tracker.status === this.selectedStatus);
      }
      
      return filtered;
    }
  },
  
  async created() {
    this.authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
      
      try {
        this.loggedInUser = user.email;
        await this.fetchUserPermissions(user.email);
      } catch (e) {
        this.handleError(e, "Loading user permissions");
      }
    });

    await Promise.all([
      this.fetchSalesTrackers(),
      this.fetchShipments()
    ]);
  },

  beforeDestroy() {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }
  },
  
  methods: {
    handleError(error, operation) {
      console.error(`Error in ${operation}:`, error);
      
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        this.$router.push('/login');
        return;
      }
      
      this.showSnackbar(`${operation} failed: ${error.message}`, "error");
    },

    async fetchUserPermissions(email) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const userData = snap.docs[0].data();
          this.userPermissions = userData || {};
          this.userRole = userData.role || '';
          this.isAdmin = userData.role === "Admin";
          
          console.log('User Permissions Loaded:', {
            email: email,
            role: this.userRole,
            salesOrderTracker: userData.salesOrderTracker,
            Add_Button: userData.salesOrderTracker?.Add_Button
          });
        } else {
          console.error('No user found for:', email);
        }
      } catch (e) {
        this.handleError(e, "Loading user permissions");
      }
    },
    
    async fetchSalesTrackers() {
      try {
        const trackerQuery = collection(db, "SalesTracker");
        const querySnapshot = await getDocs(trackerQuery);
        const trackers = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          const updates = data.supplierUpdates || [];
          const latestUpdate = updates.length > 0 
            ? updates.sort((a, b) => b.timestamp - a.timestamp)[0]
            : null;
            
          trackers.push({
            id: doc.id,
            salesOrderNumber: data.salesOrderNumber || 'N/A',
            supplierName: data.supplierName || 'N/A',
            customerName: data.customerName || 'N/A',
            status: data.status || 'N/A',
            method: data.method || 'N/A',
            arrivalDate: data.arrivalDate || 'N/A',
            orderPlacedDate: data.orderPlacedDate || 'N/A',
            poNumber: data.poNumber || 'N/A',
            salesperson: data.salesperson?.fullName || '',
            engineer: data.engineer?.fullName || '',
            pdfUrl: data.pdfUrl || null,
            supplierUpdates: updates
          });
        });
        trackers.sort((a, b) => {
          const aNum = parseInt(a.salesOrderNumber) || 0;
          const bNum = parseInt(b.salesOrderNumber) || 0;
          return bNum - aNum;
        });
        this.trackers = trackers;
      } catch (error) {
        this.handleError(error, "Loading SO trackers");
      } finally {
        this.loading = false;
      }
    },
    
    async fetchShipments() {
      try {
        const shipmentQuery = collection(db, "shipments");
        const querySnapshot = await getDocs(shipmentQuery);
        this.shipments = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          this.shipments.push({
            id: doc.id,
            shipmentNumber: data.shipmentNumber,
            linkedSONumbers: data.linkedSONumbers || [],
            status: data.status,
            latestUpdateFromSupplier: data.latestUpdateFromSupplier || []
          });
        });
      } catch (error) {
        this.handleError(error, "Loading shipments");
      }
    },
    
    getLinkedShipments(soNumber) {
      const linked = this.shipments.filter(shipment => 
        shipment.linkedSONumbers.includes(soNumber)
      );
      if (linked.length === 0) return "None";
      
      return linked.map(s => {
        if (s.shipmentNumber === 'OPEN_ORDER') {
          return 'Open Order (Default)';
        }
        return `#${s.shipmentNumber}`;
      }).join(", ");
    },

    getCombinedUpdates(tracker) {
      const soUpdates = (tracker.supplierUpdates || []).map(update => {
        const isShipmentStatusUpdate = update.text && (
          update.text.includes('Status updated to') && 
          update.text.includes('from Shipment')
        );
        
        let extractedShipmentNumber = null;
        if (isShipmentStatusUpdate) {
          const match = update.text.match(/from Shipment #?(\w+)/);
          extractedShipmentNumber = match ? match[1] : null;
        }
        
        return {
          ...update,
          source: isShipmentStatusUpdate ? 'Shipment' : 'SO',
          shipmentNumber: isShipmentStatusUpdate ? extractedShipmentNumber : null,
          timestamp: this.parseTimestamp(update.timestamp)
        };
      });
      
      const linkedShipments = this.shipments.filter(shipment => 
        shipment.linkedSONumbers.includes(tracker.salesOrderNumber)
      );
      
      const shipmentUpdates = [];
      linkedShipments.forEach(shipment => {
        if (shipment.latestUpdateFromSupplier && Array.isArray(shipment.latestUpdateFromSupplier)) {
          shipment.latestUpdateFromSupplier.forEach(update => {
            shipmentUpdates.push({
              ...update,
              source: 'Shipment',
              shipmentNumber: shipment.shipmentNumber,
              timestamp: this.parseTimestamp(update.timestamp)
            });
          });
        }
      });
      
      return [...soUpdates, ...shipmentUpdates]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 5);
    },

    parseTimestamp(timestamp) {
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        return timestamp.seconds * 1000;
      }
      if (typeof timestamp === 'number') return timestamp;
      if (typeof timestamp === 'string') {
        const parsed = Date.parse(timestamp);
        return isNaN(parsed) ? Date.now() : parsed;
      }
      return Date.now();
    },

    formatUpdateDate(timestamp) {
      if (!timestamp) return '';
      try {
        const parsedTimestamp = this.parseTimestamp(timestamp);
        const date = new Date(parsedTimestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString('en-ZA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return 'Invalid Date';
      }
    },
    
    getCustomerName(tracker) {
      return tracker.customerName || 'Customer Name';
    },
    
    getStatusClass(status) {
      const classMap = {
        'Open Order': 'status-open',
        'Referenced': 'status-referenced',
        'Booked': 'status-booked',
        'Manufacturing': 'status-manufacturing',
        'Partial Supply': 'status-partial',
        'In Transit': 'status-transit',
        'Docked': 'status-docked',
        'At Airport': 'status-airport',
        'On Route to JHB': 'status-route',
        'Complete': 'status-complete',
        'Delivered': 'status-delivered'
      };
      return classMap[status] || 'status-default';
    },
    
    getStatusColor(status) {
      const colorMap = {
        'Open Order': 'indigo',
        'Referenced': 'orange',
        'Booked': 'purple',
        'Manufacturing': 'amber',
        'Partial Supply': 'deep-orange',
        'In Transit': 'pink',
        'Docked': 'teal',
        'At Airport': 'cyan',
        'On Route to JHB': 'light-green',
        'Complete': 'success',
        'Delivered': 'success'
      };
      return colorMap[status] || '#ff3b47';
    },

    canEditTracker(tracker) {
      if (!this.canEditSalesTracker) {
        return false;
      }
      
      const isLocked = tracker.status === 'Complete' || tracker.status === 'Delivered';
      
      if (isLocked && this.userRole !== 'Admin') {
        return false;
      }
      
      return true;
    },

    handleCardClick(tracker) {
      if (!this.canEditTracker(tracker)) {
        if (tracker.status === 'Complete' || tracker.status === 'Delivered') {
          this.showSnackbar(`Cannot edit SO - Status is ${tracker.status}. Only Admin can modify completed SOs.`, "warning");
        } else {
          this.showSnackbar("You don't have permission to edit SO trackers.", "warning");
        }
        return;
      }
      
      this.openSalesTracker(tracker);
    },
    
    openInfoModal(tracker) {
      this.infoDialog.tracker = tracker;
      this.infoDialog.visible = true;
    },
    
    viewPDF(pdfUrl) {
      window.open(pdfUrl, '_blank');
    },
    
    openSalesTracker(tracker) {
      if (tracker.id) {
        localStorage.setItem('selectedSalesTrackerId', tracker.id);
        this.$router.push({ name: 'EditSalesTracker' });
      }
    },
    
    navigateToAddSalesTracker() {
      this.$router.push({ name: 'AddSalesTracker' });
    },
    
    confirmBackNavigation() {
      this.backDialog = true;
    },
    
    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },
    
    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    
    copyModalContent(tracker) {
      const combinedUpdates = this.getCombinedUpdates(tracker);
      let updatesText = '';
      
      if (combinedUpdates.length > 0) {
        updatesText = '\n\nLatest Updates:\n' + 
          combinedUpdates.map(update => {
            const source = update.source;
            const shipmentInfo = update.shipmentNumber ? ` - ${update.shipmentNumber === 'OPEN_ORDER' ? 'Open Order' : `Shipment #${update.shipmentNumber}`}` : '';
            return `${source}${shipmentInfo} (${this.formatUpdateDate(update.timestamp)}): ${update.text} - By: ${update.user}`;
          }).join('\n');
      }

      const content = `SO Number: ${tracker.salesOrderNumber || 'N/A'}
Customer: ${this.getCustomerName(tracker)}
PO Number: ${tracker.poNumber || 'N/A'}
Salesperson: ${tracker.salesperson || 'N/A'}
Engineer: ${tracker.engineer || 'N/A'}
Method: ${tracker.method || 'N/A'}
Order Placed: ${tracker.orderPlacedDate || 'N/A'}
Expected Arrival: ${tracker.arrivalDate || 'N/A'}
Status: ${tracker.status || 'Unknown'}
Linked Shipments: ${this.getLinkedShipments(tracker.salesOrderNumber)}${updatesText}`;
      
      navigator.clipboard.writeText(content)
        .then(() => {
          this.showSnackbar("SO details copied successfully.");
        })
        .catch((err) => {
          this.showSnackbar("Failed to copy SO details.", "error");
        });
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
span,
v-card-title,
v-row,
v-col,
v-card {
  font-family: "Montserrat", sans-serif;
}

.sales-orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.so-card {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  min-height: 280px;
  display: flex;
  flex-direction: column;
}

.so-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: #4a5568;
}

.so-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.1);
  border-color: #ddd;
}

.so-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px 16px;
  margin-top: 8px;
}

.info-btn {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.info-btn:hover {
  background: #edf2f7;
  transform: scale(1.1);
}

.so-card-content {
  padding: 0 16px;
  flex: 1;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  align-items: flex-start;
}

.info-label {
  color: #4a5568;
  font-weight: 600;
  font-size: 13px;
  min-width: 90px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  color: #2d3748;
  font-size: 13px;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

.so-card-actions {
  padding: 16px;
  margin-top: auto;
}

.status-chip {
  font-weight: 600 !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.so-number-chip {
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.5px;
}

/* Status Classes */
.status-open { background: rgba(99, 102, 241, 0.2); color: #6366f1; border: 1px solid rgba(99, 102, 241, 0.3); }
.status-referenced { background: #f56500; color: white; }
.status-booked { background: #805ad5; color: white; }
.status-manufacturing { background: #d69e2e; color: white; }
.status-partial { background: #dd6b20; color: white; }
.status-transit { background: #48bb78; color: white; }
.status-docked { background: #319795; color: white; }
.status-airport { background: #0bc5ea; color: white; }
.status-route { background: #68d391; color: white; }
.status-complete { background: #48bb78; color: white; }
.status-delivered { background: #48bb78; color: white; }
.status-default { background: #718096; color: white; }

/* Modal Styles */
.modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-field.full-width {
  grid-column: 1 / -1;
}

.modal-field-label {
  color: #4a5568;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-field-value {
  color: #2d3748;
  font-size: 14px;
  word-break: break-word;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.no-results {
  color: #4a5568;
  padding: 40px 20px;
}

/* Combined Updates Styles */
.combined-updates {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  background: #f8fafc;
}

.update-item {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  border-left: 4px solid #4a5568;
}

.update-item:last-child {
  margin-bottom: 0;
}

.update-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 4px;
}

.update-source {
  font-weight: 600 !important;
  font-size: 11px !important;
}

.update-shipment {
  font-weight: 600;
  color: #4a5568;
  font-size: 12px;
}

.update-date {
  font-size: 11px;
  color: #718096;
}

.update-text {
  color: #2d3748;
  font-size: 13px;
  margin-bottom: 4px;
  line-height: 1.4;
}

.update-user {
  font-size: 11px;
  color: #718096;
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sales-orders-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-grid {
    grid-template-columns: 1fr;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
  }
  
  .info-label,
  .info-value {
    text-align: left;
    max-width: 100%;
  }

  .update-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>